import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { PhotoThumb } from "../equipment/shared/PhotoThumb";
import TripFormDialog from "./TripFormDialog";
import LocationUpdateDialog from "./LocationUpdateDialog";
import TripDocumentDialog from "./TripDocumentDialog";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">{label}</p>
      <p className="text-sm text-slate-800 font-medium mt-0.5">{value ?? "—"}</p>
    </div>
  );
}

function ManualOverride({ tripId, updateId, field, label, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (value === "" || parseFloat(value) < 0) return toast.error("Enter a valid distance");
    setSaving(true);
    try {
      const res = await client.patch(`/transport-log/trips/${tripId}/location-updates/${updateId}/manual-override`, {
        [field]: parseFloat(value),
      });
      toast.success(`${label} saved`);
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-1">
      <Input
        type="number"
        step="any"
        placeholder="Manual km"
        className="w-28 h-8 text-xs"
        data-testid={`location-${field}-manual-input-${updateId}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <Button size="sm" variant="outline" className="h-8" disabled={saving} data-testid={`location-${field}-manual-save-${updateId}`} onClick={save}>
        Save
      </Button>
    </div>
  );
}

export default function TripDetailPanel({ tripId, sites, rigs, employees, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get(`/transport-log/trips/${tripId}`)
      .then((res) => setData(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tripId]);

  const employeeName = (id) => employees.find((e) => e.employee_id === id)?.name || id;

  const markCompleted = async () => {
    setBusy(true);
    try {
      const res = await client.put(`/transport-log/trips/${tripId}`, { status: "Completed" });
      toast.success("Trip marked as Completed");
      setData((d) => ({ ...d, trip: res.data }));
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteLocationUpdate = async (updateId) => {
    setBusy(true);
    try {
      const res = await client.delete(`/transport-log/trips/${tripId}/location-updates/${updateId}`);
      toast.success("Location update deleted");
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await client.get(`/transport-log/trips/${tripId}/documents/${doc.doc_id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (loading || !data) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="trip-detail-panel">
          <p className="text-sm text-slate-400 py-8 text-center">Loading...</p>
        </SheetContent>
      </Sheet>
    );
  }

  const { trip, location_updates } = data;
  const isCreator = trip.entered_by === user?.employee_id;
  const canEdit = isCreator && trip.status === "In Transit";

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="trip-detail-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {trip.trip_id}
            <Badge className={trip.status === "Completed" ? "bg-emerald-600" : "bg-amber-500"}>{trip.status}</Badge>
          </SheetTitle>
          <SheetDescription>
            {trip.trip_type} Trip · {trip.from_site_label} → {trip.to_site_label || trip.to_site_free_text}
          </SheetDescription>
          <div className="flex gap-2 pt-2">
            {canEdit && (
              <Button size="sm" data-testid="trip-detail-edit-button" onClick={() => setShowEdit(true)} className="bg-amber-600 hover:bg-amber-700">
                Edit
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" disabled={busy} data-testid="trip-detail-mark-completed-button" onClick={markCompleted}>
                Mark Completed
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-4 pt-6">
          <Field label="Final Destination Reference" value={trip.final_destination_reference} />
          <Field label="Start Date" value={new Date(trip.start_date).toLocaleString("en-IN")} />
          {trip.trip_type === "Rig" ? (
            <Field label="Rig" value={trip.rig_label} />
          ) : (
            <Field label="Items" value={(trip.items || []).map((i) => `${i.description}${i.quantity ? ` (${i.quantity})` : ""}`).join(", ")} />
          )}
          <Field label="Driver" value={trip.driver_name ? `${trip.driver_name} · ${trip.driver_phone || "—"}` : null} />
          <Field label="Transporter" value={trip.transporter_name} />
          <Field label="Agreed Transport Cost" value={trip.agreed_transport_cost != null ? `₹${trip.agreed_transport_cost}` : null} />
          <Field label="Entered By" value={trip.entered_by_name} />
          <Field label="Accompanying Staff" value={trip.accompanying_staff?.length ? trip.accompanying_staff.map(employeeName).join(", ") : null} />
        </div>

        <div className="pt-6 border-t border-slate-200 mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Location Updates</p>
            {trip.status === "In Transit" && (
              <Button size="sm" variant="outline" data-testid="trip-detail-add-location-button" onClick={() => setShowLocationForm(true)}>
                Add Location Update
              </Button>
            )}
          </div>
          <div className="space-y-3" data-testid="trip-location-updates-list">
            {location_updates.length === 0 && <p className="text-sm text-slate-400">No location updates yet.</p>}
            {location_updates.map((u) => (
              <div key={u.id} className="rounded-md border border-slate-200 p-3 space-y-1.5" data-testid={`location-update-row-${u.id}`}>
                <div className="flex justify-between items-start">
                  <p className="text-sm font-medium text-slate-800">{u.location_reference}</p>
                  {trip.status === "In Transit" && (
                    <button
                      data-testid={`location-delete-button-${u.id}`}
                      className="text-xs text-red-500 hover:underline"
                      onClick={() => deleteLocationUpdate(u.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  {u.entered_by_name} · {new Date(u.created_at).toLocaleString("en-IN")}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Distance Travelled: </span>
                    {u.distance_travelled_manual_km ?? u.distance_travelled_auto_km ?? (u.distance_travelled_failed ? "Calc failed" : "—")}
                    {u.distance_travelled_failed && u.distance_travelled_error && (
                      <p className="text-[11px] text-slate-400" data-testid={`distance-travelled-error-${u.id}`}>{u.distance_travelled_error}</p>
                    )}
                    {u.distance_travelled_failed && u.distance_travelled_manual_km == null && (
                      <ManualOverride tripId={tripId} updateId={u.id} field="distance_travelled_manual_km" label="Distance Travelled" onSaved={load} />
                    )}
                  </div>
                  <div>
                    <span className="text-slate-500">Distance Remaining: </span>
                    {u.distance_remaining_manual_km ?? u.distance_remaining_auto_km ?? (u.distance_remaining_failed ? "Calc failed" : "—")}
                    {u.distance_remaining_failed && u.distance_remaining_error && (
                      <p className="text-[11px] text-slate-400" data-testid={`distance-remaining-error-${u.id}`}>{u.distance_remaining_error}</p>
                    )}
                    {u.distance_remaining_failed && u.distance_remaining_manual_km == null && (
                      <ManualOverride tripId={tripId} updateId={u.id} field="distance_remaining_manual_km" label="Distance Remaining" onSaved={load} />
                    )}
                  </div>
                </div>
                {u.remarks && <p className="text-xs text-slate-600">{u.remarks}</p>}
                {u.photo && (
                  <PhotoThumb path={`/transport-log/trips/${tripId}/location-updates/${u.id}/photo`} alt="Location photo" testId={`location-photo-${u.id}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="pt-6 border-t border-slate-200 mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Documents</p>
            <Button size="sm" variant="outline" data-testid="trip-detail-add-document-button" onClick={() => setShowDocForm(true)}>
              Add Document
            </Button>
          </div>
          <div className="space-y-2" data-testid="trip-documents-list">
            {(trip.documents || []).length === 0 && <p className="text-sm text-slate-400">No documents uploaded yet.</p>}
            {(trip.documents || []).map((doc) => (
              <div key={doc.doc_id} className="flex justify-between items-center rounded-md border border-slate-200 p-2.5" data-testid={`trip-document-row-${doc.doc_id}`}>
                <div>
                  <p className="text-sm text-slate-800">
                    {doc.document_type}
                    {doc.label ? ` — ${doc.label}` : ""}
                  </p>
                  <p className="text-xs text-slate-400">
                    {doc.uploaded_by_name} · {new Date(doc.uploaded_at).toLocaleDateString("en-IN")}
                  </p>
                </div>
                <button className="text-xs text-amber-600 hover:underline" data-testid={`trip-document-view-${doc.doc_id}`} onClick={() => viewDocument(doc)}>
                  View
                </button>
              </div>
            ))}
          </div>
        </div>
      </SheetContent>

      {showEdit && (
        <TripFormDialog
          trip={trip}
          sites={sites}
          rigs={rigs}
          employees={employees}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            load();
            onChanged();
          }}
        />
      )}
      {showLocationForm && (
        <LocationUpdateDialog
          tripId={tripId}
          onClose={() => setShowLocationForm(false)}
          onSaved={() => {
            setShowLocationForm(false);
            load();
            onChanged();
          }}
        />
      )}
      {showDocForm && (
        <TripDocumentDialog
          tripId={tripId}
          onClose={() => setShowDocForm(false)}
          onSaved={() => {
            setShowDocForm(false);
            load();
          }}
        />
      )}
    </Sheet>
  );
}
